import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit, FiSend, FiCheck, FiX, FiPackage,
  FiDownload, FiCopy, FiAlertCircle, FiCalendar, FiUser,
  FiFileText, FiDollarSign, FiClock, FiTruck, FiShoppingCart,
  FiMapPin, FiPhone, FiMail
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Modal } from '../../components/UI/Modal';
import { useCompras } from '../../hooks/useCompras';
import { proveedoresService } from '../../services';

const statusConfig = {
  DRAFT: {
    label: 'Borrador',
    color: 'secondary',
    icon: FiEdit,
    description: 'Orden en proceso de creación'
  },
  PENDING: {
    label: 'Pendiente',
    color: 'warning',
    icon: FiClock,
    description: 'Orden pendiente de enviar'
  },
  SENT: {
    label: 'Enviada',
    color: 'info',
    icon: FiSend,
    description: 'Orden enviada al proveedor'
  },
  CONFIRMED: {
    label: 'Confirmada',
    color: 'success',
    icon: FiCheck,
    description: 'Orden confirmada por el proveedor'
  },
  PARTIAL_RECEIVED: {
    label: 'Parcialmente Recibida',
    color: 'warning',
    icon: FiPackage,
    description: 'Algunos productos han sido recibidos'
  },
  RECEIVED: {
    label: 'Recibida',
    color: 'success',
    icon: FiCheck,
    description: 'Todos los productos han sido recibidos'
  },
  CANCELLED: {
    label: 'Cancelada',
    color: 'danger',
    icon: FiX,
    description: 'Orden cancelada'
  }
};

const priorityConfig = {
  LOW: { label: 'Baja', color: 'secondary' },
  NORMAL: { label: 'Normal', color: 'info' },
  HIGH: { label: 'Alta', color: 'warning' },
  URGENT: { label: 'Urgente', color: 'danger' }
};

const CompraDetalle = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    getOrdenById,
    enviarOrden,
    aprobarOrden,
    cancelarOrden,
    recibirOrden,
    duplicarOrden,
    marcarComoUrgente
  } = useCompras();

  const [orden, setOrden] = useState(null);
  const [proveedor, setProveedor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    emails: [],
    subject: '',
    message: '',
    includePdf: true
  });
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveData, setReceiveData] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    notes: '',
    received_items: []
  });

  useEffect(() => {
    loadOrdenDetalle();
  }, [id]);

  const loadOrdenDetalle = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getOrdenById(id);

      if (result.success) {
        console.log('Orden cargada:', result.data);
        console.log('Items:', result.data.items);
        setOrden(result.data);

        // Cargar datos del proveedor
        if (result.data.supplier_id) {
          const proveedorResult = await proveedoresService.getProveedor(result.data.supplier_id);
          if (proveedorResult.success) {
            setProveedor(proveedorResult.data);

            // Pre-cargar email del proveedor si existe
            if (proveedorResult.data.email) {
              setEmailData(prev => ({
                ...prev,
                emails: [proveedorResult.data.email],
                subject: `Orden de Compra ${result.data.order_number || '#' + id}`,
                message: `Estimado proveedor,\n\nAdjuntamos la orden de compra ${result.data.order_number || '#' + id} para su revisión y confirmación.\n\nSaludos cordiales.`
              }));
            }
          }
        }
      } else {
        setError(result.error || 'Orden de compra no encontrada');
      }
    } catch (error) {
      setError('Error de conexión al cargar orden de compra');
      console.error('Error loading purchase order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (actionFn, actionName) => {
    setIsProcessing(true);
    try {
      const result = await actionFn(id);
      if (result.success) {
        await loadOrdenDetalle(); // Recargar datos
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError(`Error al ${actionName}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDuplicate = async () => {
    setIsProcessing(true);
    try {
      const result = await duplicarOrden(id);
      if (result.success) {
        navigate(`/system/compras/${result.data.id}/editar`);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Error al duplicar orden');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendEmail = async () => {
    setIsProcessing(true);
    try {
      const result = await enviarOrden(id, {
        email_addresses: emailData.emails,
        subject: emailData.subject || null,
        message: emailData.message || null,
        include_pdf: emailData.includePdf !== false
      });

      if (result.success) {
        setShowEmailModal(false);
        await loadOrdenDetalle(); // Recargar para actualizar estado
      } else {
        setError(result.error || 'Error al enviar email');
      }
    } catch (error) {
      setError('Error al enviar email');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddEmail = (email) => {
    if (email && !emailData.emails.includes(email)) {
      setEmailData(prev => ({
        ...prev,
        emails: [...prev.emails, email]
      }));
    }
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailData(prev => ({
      ...prev,
      emails: prev.emails.filter(e => e !== emailToRemove)
    }));
  };

  const handleOpenReceiveModal = () => {
    // Inicializar received_items con los items de la orden
    if (orden && orden.items) {
      const initialItems = orden.items.map(item => ({
        item_id: item.id,
        received_quantity: item.pending_quantity || item.quantity || 0,
        max_quantity: item.pending_quantity || item.quantity || 0,
        product_name: item.description || `Producto #${item.product_id}`
      }));

      setReceiveData(prev => ({
        ...prev,
        received_items: initialItems
      }));
    }
    setShowReceiveModal(true);
  };

  const handleReceiveSubmit = async () => {
    setIsProcessing(true);
    try {
      // Preparar datos para el endpoint
      const submitData = {
        receipt_date: receiveData.delivery_date,
        notes: receiveData.notes || 'Recibido completo',
        items: receiveData.received_items.map(item => ({
          item_id: item.item_id,
          received_quantity: parseFloat(item.received_quantity) || 0
        }))
      };

      const result = await recibirOrden(id, submitData);

      if (result.success) {
        setShowReceiveModal(false);
        await loadOrdenDetalle(); // Recargar para actualizar estado
      } else {
        setError(result.error || 'Error al recibir orden');
      }
    } catch (error) {
      setError('Error al recibir orden');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateReceivedQuantity = (itemId, quantity) => {
    setReceiveData(prev => ({
      ...prev,
      received_items: prev.received_items.map(item =>
        item.item_id === itemId
          ? { ...item, received_quantity: quantity }
          : item
      )
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('es-CO', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusInfo = (status) => {
    return statusConfig[status] || statusConfig.DRAFT;
  };

  const getPriorityInfo = (priority) => {
    return priorityConfig[priority] || priorityConfig.NORMAL;
  };

  const canEdit = () => {
    return orden && ['DRAFT', 'PENDING'].includes(orden.status);
  };

  const canSend = () => {
    return orden && ['DRAFT', 'PENDING'].includes(orden.status);
  };

  const canApprove = () => {
    return orden && orden.status === 'SENT';
  };

  const canReceive = () => {
    return orden && ['CONFIRMED', 'PARTIAL_RECEIVED'].includes(orden.status);
  };

  const canCancel = () => {
    return orden && !['RECEIVED', 'CANCELLED'].includes(orden.status);
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando orden de compra..." />;
  }

  if (error && !orden) {
    return (
      <div className="error-container">
        <h2>Orden no encontrada</h2>
        <p>{error}</p>
        <button
          className="btn btn--primary"
          onClick={() => navigate('/system/compras')}
        >
          <FiArrowLeft /> Volver a Órdenes de Compra
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(orden.status);
  const priorityInfo = getPriorityInfo(orden.priority);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/compras')}
        >
          <FiArrowLeft /> Volver a Órdenes
        </button>

        <h1 className="form-title">
          {orden.order_number || `ORDEN #${id}`}
        </h1>

        <div className="form-actions">
          {canEdit() && (
            <button
              className="btn btn--outline"
              onClick={() => navigate(`/system/compras/${id}/editar`)}
            >
              <FiEdit /> Editar
            </button>
          )}

          <button
            className="btn btn--outline"
            onClick={handleDuplicate}
            disabled={isProcessing}
          >
            <FiCopy /> Duplicar
          </button>

          <button className="btn btn--outline">
            <FiDownload /> Descargar PDF
          </button>

          {canSend() && (
            <button
              className="btn btn--primary"
              onClick={() => setShowEmailModal(true)}
              disabled={isProcessing}
            >
              <FiSend /> Enviar al Proveedor
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setError(null)}
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Status Banner */}
      <div className={`status-banner status-banner--${statusInfo.color}`} style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StatusIcon size={24} />
          <div>
            <div style={{ fontWeight: '600', fontSize: '1.1em' }}>{statusInfo.label}</div>
            <div style={{ fontSize: '0.9em', opacity: 0.9 }}>{statusInfo.description}</div>
          </div>
        </div>
        {orden.priority !== 'NORMAL' && (
          <span className={`badge badge--${priorityInfo.color}`} style={{ fontSize: '0.95em', padding: '6px 12px' }}>
            <FiAlertCircle size={14} style={{ marginRight: '4px' }} />
            Prioridad: {priorityInfo.label}
          </span>
        )}
      </div>

      {/* Contenido en formato de formulario (solo lectura) */}
      <div className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Información del Proveedor */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Información del Proveedor</h2>
              </div>

              <div className="form-group">
                <label className="form-label">ID del Proveedor</label>
                <div className="form-value" style={{ fontWeight: '600', fontSize: '1.05em' }}>
                  Proveedor #{orden.supplier_id || 'N/A'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Creado por</label>
                <div className="form-value">Usuario #{orden.created_by || 'N/A'}</div>
              </div>

              {orden.approved_by && (
                <div className="form-group">
                  <label className="form-label">Aprobado por</label>
                  <div className="form-value">Usuario #{orden.approved_by}</div>
                </div>
              )}

              {orden.sent_by && (
                <div className="form-group">
                  <label className="form-label">Enviado por</label>
                  <div className="form-value">Usuario #{orden.sent_by}</div>
                </div>
              )}
            </div>

            {/* Fechas */}
            <div className="form-section">
              <div className="section-header">
                <FiCalendar className="section-icon" />
                <h3 className="section-title">Fechas Importantes</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Fecha de Orden</label>
                <div className="form-value">{formatDate(orden.order_date)}</div>
              </div>

              {orden.expected_delivery_date && (
                <div className="form-group">
                  <label className="form-label">
                    <FiTruck size={14} style={{ marginRight: '6px' }} />
                    Entrega Esperada
                  </label>
                  <div className="form-value">{formatDate(orden.expected_delivery_date)}</div>
                </div>
              )}

              {orden.delivery_date && (
                <div className="form-group">
                  <label className="form-label">Fecha de Entrega</label>
                  <div className="form-value">{formatDate(orden.delivery_date)}</div>
                </div>
              )}

              {orden.sent_date && (
                <div className="form-group">
                  <label className="form-label">Fecha de Envío</label>
                  <div className="form-value">{formatDate(orden.sent_date)}</div>
                </div>
              )}

              {orden.confirmation_date && (
                <div className="form-group">
                  <label className="form-label">Fecha de Confirmación</label>
                  <div className="form-value">{formatDate(orden.confirmation_date)}</div>
                </div>
              )}

              {orden.approved_date && (
                <div className="form-group">
                  <label className="form-label">Fecha de Aprobación</label>
                  <div className="form-value">{formatDate(orden.approved_date)}</div>
                </div>
              )}
            </div>

            {/* Resumen Financiero */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h3 className="section-title">Resumen Financiero</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Subtotal</label>
                <div className="form-value">{formatCurrency(orden.subtotal)}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Impuestos ({parseFloat(orden.tax_rate || 0).toFixed(2)}%)</label>
                <div className="form-value">{formatCurrency(orden.tax_amount)}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Costo de Envío</label>
                <div className="form-value">{formatCurrency(orden.shipping_cost)}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Descuento</label>
                <div className="form-value" style={{ color: parseFloat(orden.discount_amount || 0) > 0 ? '#10b981' : 'inherit' }}>
                  {parseFloat(orden.discount_amount || 0) > 0 ? '-' : ''}{formatCurrency(orden.discount_amount)}
                </div>
              </div>

              <div className="form-group" style={{ borderTop: '2px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                <label className="form-label" style={{ fontSize: '1.1em', fontWeight: '600' }}>Total</label>
                <div className="form-value" style={{ fontSize: '1.3em', fontWeight: '700', color: '#1f2937' }}>
                  {formatCurrency(orden.total_amount)}
                </div>
              </div>
            </div>

            {/* Estado de Recepción */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h3 className="section-title">Estado de Recepción</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Estado</label>
                <div className="form-value">
                  <span className={`badge badge--${orden.is_fully_received ? 'success' : 'warning'}`}>
                    {orden.is_fully_received ? 'Completamente Recibida' : 'Pendiente de Recepción'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Número de Orden</label>
                <div className="form-value" style={{ fontWeight: '600' }}>{orden.order_number}</div>
              </div>

              <div className="form-group">
                <label className="form-label">ID de Orden</label>
                <div className="form-value">#{orden.id}</div>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Productos */}
            <div className="form-section">
              <div className="section-header">
                <FiShoppingCart className="section-icon" />
                <h2 className="section-title">
                  Productos ({orden.items?.length || 0} items)
                </h2>
              </div>

              {orden.items && orden.items.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                  {orden.items.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px',
                        marginBottom: '10px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {item.description || `Producto #${item.product_id}`}
                          </div>
                          {item.supplier_product_code && (
                            <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                              Código: {item.supplier_product_code}
                            </div>
                          )}
                          {item.product_id && (
                            <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                              ID Producto: {item.product_id}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '600' }}>
                            {formatCurrency(item.total_amount)}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', color: '#6b7280' }}>
                        <span>Cantidad: {parseFloat(item.quantity)}</span>
                        <span>Precio unit: {formatCurrency(item.unit_cost)}</span>
                      </div>
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <span className={`badge badge--${item.is_received ? 'success' : 'secondary'}`} style={{ fontSize: '0.85em' }}>
                          {item.is_received ? 'Completamente Recibido' : 'Pendiente'}
                        </span>
                        {parseFloat(item.received_quantity) > 0 && !item.is_received && (
                          <span className="badge badge--warning" style={{ fontSize: '0.85em' }}>
                            Recibido: {parseFloat(item.received_quantity)}/{parseFloat(item.quantity)}
                          </span>
                        )}
                        {parseFloat(item.pending_quantity) > 0 && (
                          <span className="badge badge--info" style={{ fontSize: '0.85em' }}>
                            Pendiente: {parseFloat(item.pending_quantity)}
                          </span>
                        )}
                      </div>
                      {item.notes && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#6b7280', fontStyle: 'italic' }}>
                          Nota: {item.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ marginTop: '16px' }}>
                  <p>No hay productos en esta orden</p>
                </div>
              )}
            </div>

            {/* Información Adicional */}
            {(orden.notes || orden.supplier_notes || orden.terms_and_conditions || orden.shipping_address) && (
              <div className="form-section">
                <div className="section-header">
                  <FiFileText className="section-icon" />
                  <h3 className="section-title">Información Adicional</h3>
                </div>

                {orden.notes && (
                  <div className="form-group">
                    <label className="form-label">Notas Internas</label>
                    <div className="form-value" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#fef3c7', padding: '12px', borderRadius: '6px', fontSize: '0.95em' }}>
                      {orden.notes}
                    </div>
                  </div>
                )}

                {orden.supplier_notes && (
                  <div className="form-group">
                    <label className="form-label">Notas para el Proveedor</label>
                    <div className="form-value" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#dbeafe', padding: '12px', borderRadius: '6px', fontSize: '0.95em' }}>
                      {orden.supplier_notes}
                    </div>
                  </div>
                )}

                {orden.terms_and_conditions && (
                  <div className="form-group">
                    <label className="form-label">Términos y Condiciones</label>
                    <div className="form-value" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f3f4f6', padding: '12px', borderRadius: '6px', fontSize: '0.95em' }}>
                      {orden.terms_and_conditions}
                    </div>
                  </div>
                )}

                {orden.shipping_address && (
                  <div className="form-group">
                    <label className="form-label">
                      <FiMapPin size={14} style={{ marginRight: '6px' }} />
                      Dirección de Envío
                    </label>
                    <div className="form-value" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '6px', fontSize: '0.95em' }}>
                      {orden.shipping_address}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Acciones Disponibles */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Acciones Disponibles</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
                {/* Cancelar Orden */}
                <button
                  className="btn btn--danger"
                  onClick={() => {
                    if (window.confirm('¿Estás seguro de que deseas cancelar esta orden?')) {
                      handleAction(cancelarOrden, 'cancelar orden');
                    }
                  }}
                  disabled={isProcessing || !canCancel()}
                  style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                >
                  <FiX size={18} style={{ marginRight: '8px' }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '600' }}>Cancelar Orden</div>
                    <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                      {canCancel() ? 'Esta acción no se puede deshacer' : 'No se puede cancelar esta orden'}
                    </div>
                  </div>
                </button>

                {/* Marcar como Recibida */}
                <button
                  className="btn btn--primary"
                  onClick={handleOpenReceiveModal}
                  disabled={!canReceive()}
                  style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                >
                  <FiPackage size={18} style={{ marginRight: '8px' }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '600' }}>Marcar como Recibida</div>
                    <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                      {canReceive() ? 'Registrar productos recibidos' : 'Orden debe estar confirmada'}
                    </div>
                  </div>
                </button>

                {/* Confirmar Orden */}
                <button
                  className="btn btn--success"
                  onClick={() => handleAction(aprobarOrden, 'aprobar orden')}
                  disabled={isProcessing || !canApprove()}
                  style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                >
                  <FiCheck size={18} style={{ marginRight: '8px' }} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '600' }}>Confirmar Orden</div>
                    <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
                      {canApprove() ? 'Aprobar y confirmar con el proveedor' : 'Orden debe estar enviada'}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para enviar email */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiMail />
            Enviar Orden al Proveedor
          </span>
        }
        size="large"
        closeOnOverlay={!isProcessing}
        closeOnEscape={!isProcessing}
        showCloseButton={!isProcessing}
      >
        {/* Información del proveedor */}
        {proveedor && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              {proveedor.name || 'Proveedor'}
            </div>
            {proveedor.email && (
              <div style={{ fontSize: '0.9em', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FiMail size={14} />
                {proveedor.email}
              </div>
            )}
          </div>
        )}

        {/* Emails */}
        <div className="form-group">
          <label className="form-label">Destinatarios *</label>

          {/* Lista de emails */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            {emailData.emails.map((email, index) => (
              <span
                key={index}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  backgroundColor: '#663399',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '0.875rem'
                }}
              >
                {email}
                <button
                  type="button"
                  onClick={() => handleRemoveEmail(email)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <FiX size={16} />
                </button>
              </span>
            ))}
          </div>

          {/* Input para agregar email */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="email"
              className="form-input"
              placeholder="Agregar email adicional"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddEmail(e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn--outline"
              onClick={(e) => {
                const input = e.target.previousSibling;
                handleAddEmail(input.value);
                input.value = '';
              }}
            >
              Agregar
            </button>
          </div>
        </div>

        {/* Asunto */}
        <div className="form-group">
          <label className="form-label">Asunto</label>
          <input
            type="text"
            className="form-input"
            value={emailData.subject}
            onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
            placeholder="Asunto del correo"
          />
        </div>

        {/* Mensaje */}
        <div className="form-group">
          <label className="form-label">Mensaje</label>
          <textarea
            className="form-input"
            rows="5"
            value={emailData.message}
            onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
            placeholder="Mensaje personalizado..."
          />
        </div>

        {/* Incluir PDF */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={emailData.includePdf}
              onChange={(e) => setEmailData(prev => ({ ...prev, includePdf: e.target.checked }))}
            />
            <span>Incluir PDF de la orden de compra</span>
          </label>
        </div>

        {/* Acciones del modal */}
        <div className="modal-footer" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn--outline"
            onClick={() => setShowEmailModal(false)}
            disabled={isProcessing}
          >
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSendEmail}
            disabled={isProcessing || emailData.emails.length === 0}
          >
            {isProcessing ? (
              <>
                <FiSend className="spin" /> Enviando...
              </>
            ) : (
              <>
                <FiSend /> Enviar Orden
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* Modal para recibir orden */}
      <Modal
        isOpen={showReceiveModal}
        onClose={() => setShowReceiveModal(false)}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiPackage />
            Recibir Productos de Orden
          </span>
        }
        size="large"
        closeOnOverlay={!isProcessing}
        closeOnEscape={!isProcessing}
        showCloseButton={!isProcessing}
      >
        {/* Fecha de recepción */}
        <div className="form-group">
          <label className="form-label">Fecha de Recepción *</label>
          <input
            type="date"
            className="form-input"
            value={receiveData.delivery_date}
            onChange={(e) => setReceiveData(prev => ({ ...prev, delivery_date: e.target.value }))}
          />
        </div>

        {/* Productos a recibir */}
        <div className="form-group">
          <label className="form-label">Productos a Recibir</label>

          {receiveData.received_items.length > 0 ? (
            <div style={{ marginTop: '12px' }}>
              <table className="table" style={{ width: '100%', marginBottom: '0' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Producto</th>
                    <th style={{ padding: '10px', textAlign: 'center', width: '150px' }}>Cantidad Pendiente</th>
                    <th style={{ padding: '10px', textAlign: 'center', width: '180px' }}>Cantidad Recibida</th>
                  </tr>
                </thead>
                <tbody>
                  {receiveData.received_items.map((item, index) => (
                    <tr key={item.item_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '500' }}>{item.product_name}</div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          backgroundColor: '#f0f9ff',
                          color: '#0369a1',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          {item.max_quantity}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <input
                          type="number"
                          className="form-input"
                          value={item.received_quantity}
                          onChange={(e) => handleUpdateReceivedQuantity(item.item_id, parseFloat(e.target.value) || 0)}
                          min="0"
                          max={item.max_quantity}
                          step="0.01"
                          style={{ textAlign: 'center' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              color: '#6b7280'
            }}>
              No hay productos para recibir
            </div>
          )}
        </div>

        {/* Notas */}
        <div className="form-group">
          <label className="form-label">Notas</label>
          <textarea
            className="form-input"
            rows="3"
            value={receiveData.notes}
            onChange={(e) => setReceiveData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Notas sobre la recepción (opcional)..."
          />
        </div>

        {/* Acciones del modal */}
        <div className="modal-footer" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn--outline"
            onClick={() => setShowReceiveModal(false)}
            disabled={isProcessing}
          >
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleReceiveSubmit}
            disabled={isProcessing || receiveData.received_items.length === 0}
          >
            {isProcessing ? (
              <>
                <FiPackage className="spin" /> Procesando...
              </>
            ) : (
              <>
                <FiPackage /> Confirmar Recepción
              </>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CompraDetalle;
